import type {
  AllTextMessages,
  MessageResult,
  SearchResultItem,
  TalkJSMessageRecord,
} from '../../GlobalMapSearch.types';

interface RunMessagesSearchArgs {
  text: string;
  messages: AllTextMessages | undefined;
  collected: SearchResultItem[];
  flush: () => void;
}

/** Hard cap on messages scanned per conversation to bound work on long chats. */
const MAX_MESSAGES_PER_CONVO = 2_000;
/** Max snippet length surfaced in the dropdown row. */
const SNIPPET_MAX_LEN = 140;
/** Number of characters to include on each side of the matched substring. */
const SNIPPET_CONTEXT = 40;

/**
 * Build a short snippet centered on the first occurrence of `lowerQuery`
 * inside `text`. Adds ellipses when we trim either end so users can tell
 * the snippet was clipped from a longer message.
 */
function buildSnippet(text: string, lowerQuery: string): string {
  const trimmed = text.trim();
  if (!trimmed) return '';
  if (trimmed.length <= SNIPPET_MAX_LEN) return trimmed;

  const lower = trimmed.toLowerCase();
  const idx = lower.indexOf(lowerQuery);
  if (idx === -1) {
    return `${trimmed.slice(0, SNIPPET_MAX_LEN - 1)}…`;
  }

  const start = Math.max(0, idx - SNIPPET_CONTEXT);
  const end = Math.min(
    trimmed.length,
    idx + lowerQuery.length + SNIPPET_CONTEXT,
  );
  const prefix = start > 0 ? '…' : '';
  const suffix = end < trimmed.length ? '…' : '';
  return `${prefix}${trimmed.slice(start, end)}${suffix}`;
}

/**
 * Synchronous filter over the in-memory `talkjsMessages` slice. For each
 * conversation we walk its message list (capped at
 * `MAX_MESSAGES_PER_CONVO`) and surface the first message whose body
 * contains the query. One row per conversation keeps the dropdown
 * legible — clicking it routes the user back to the matching call.
 *
 * Empty queries (<3 chars) and missing/empty message state both
 * short-circuit so we don't churn on initial keystrokes.
 */
export function runMessagesSearch({
  text,
  messages,
  collected,
  flush,
}: RunMessagesSearchArgs): void {
  if (!messages) return;
  const lowerQuery = text.trim().toLowerCase();
  if (lowerQuery.length < 3) return;

  let appended = false;
  for (const conversationMessages of Object.values(messages)) {
    if (!conversationMessages || conversationMessages.length === 0) continue;

    const limit = Math.min(conversationMessages.length, MAX_MESSAGES_PER_CONVO);
    let match: TalkJSMessageRecord | null = null;
    let matchedText = '';
    for (let i = 0; i < limit; i++) {
      const msg = conversationMessages[i];
      const body = msg?.text;
      if (!body) continue;
      if (!body.toLowerCase().includes(lowerQuery)) continue;
      match = msg;
      matchedText = buildSnippet(body, lowerQuery);
      break;
    }

    if (!match) continue;

    const result: MessageResult = {
      callId: match.callerId,
      callerId: match.callerId,
      source: match.source,
      matchedText,
    };

    collected.push({ type: 'message', data: result });
    appended = true;
  }

  if (appended) flush();
}
